import type { FileStatusId } from '@steerfi/shared';
import { FILE_STATUS, FILE_STATUS_DISPLAY } from '@steerfi/shared';
import { ArrowDownCircle, ArrowUpCircle } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getDisbursementFiles } from '@/api/disbursements';
import {
  getDownloadUrl,
  getPaymentFiles,
  type PaginatedPaymentFilesResponse,
  type PaymentFileItem,
} from '@/api/file-upload-api';
import { DisbursementFilesTable } from '@/components/disbursement-files-table';
import { PaymentFilesTable } from '@/components/payment-files-table';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useKeycloakAuth } from '@/contexts/keycloak-auth-context';
import type { DisbursementFile } from '@/types/disbursement';

const DISBURSEMENT_STATUS_FILTER_ALL = 'all' as const;
const DISBURSEMENT_STATUS_FILTER_OPTIONS: ReadonlyArray<FileStatusId> = [
  FILE_STATUS.READY_FOR_RELEASE,
  FILE_STATUS.RELEASED,
  FILE_STATUS.RELEASE_FAILED,
  FILE_STATUS.FAILED,
  FILE_STATUS.CANCELLED,
];

type DisbursementStatusFilter = typeof DISBURSEMENT_STATUS_FILTER_ALL | FileStatusId;

type CollectionPresetKey = 'all' | 'awaiting-funds';
type ActivePreset = CollectionPresetKey;

interface CollectionPreset {
  label: string;
  status_type_ids?: FileStatusId[];
}

const COLLECTION_PRESETS: Record<CollectionPresetKey, CollectionPreset> = {
  all: {
    label: 'All Collections',
  },
  'awaiting-funds': {
    label: 'Awaiting Funds',
    status_type_ids: [FILE_STATUS.AWAITING_FUNDS],
  },
};

const COLLECTION_PRESET_ORDER: CollectionPresetKey[] = ['all', 'awaiting-funds'];

interface CollectionsFilters {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  status_type_id?: FileStatusId;
  party_id?: string;
}

/**
 * Escrow page for managing client collections and supplier disbursements.
 * Collections section: ACH pulls, reverse wire transfers, and awaiting funding.
 * Disbursements section: Track outbound payments to suppliers.
 */
export const Escrow: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useKeycloakAuth();
  const navigate = useNavigate();

  const [activePreset, setActivePreset] = useState<ActivePreset>('all');
  const [filters, setFilters] = useState<CollectionsFilters>({
    page: 1,
    limit: 10,
    sortBy: 'created_at',
    sortOrder: 'desc',
  });
  const [collectionsData, setCollectionsData] = useState<PaginatedPaymentFilesResponse | undefined>(
    undefined
  );
  const [collectionsLoading, setCollectionsLoading] = useState(true);
  const [collectionsError, setCollectionsError] = useState<string | null>(null);

  const [disbursementFiles, setDisbursementFiles] = useState<DisbursementFile[]>([]);
  const [disbursementFilesLoading, setDisbursementFilesLoading] = useState(false);
  const [disbursementStatusFilter, setDisbursementStatusFilter] =
    useState<DisbursementStatusFilter>(DISBURSEMENT_STATUS_FILTER_ALL);

  const fetchDisbursementFiles = useCallback(
    async (statusFilter: DisbursementStatusFilter, silent = false) => {
      if (!silent) setDisbursementFilesLoading(true);
      try {
        const response = await getDisbursementFiles({
          limit: 100,
          ...(statusFilter !== DISBURSEMENT_STATUS_FILTER_ALL && {
            status: String(statusFilter),
          }),
        });
        setDisbursementFiles(response.data);
      } catch {
        // Error loading disbursement files
      } finally {
        if (!silent) setDisbursementFilesLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchDisbursementFiles(disbursementStatusFilter);
    }
  }, [isAuthenticated, authLoading, fetchDisbursementFiles, disbursementStatusFilter]);

  const fetchCollections = useCallback(async (currentFilters: CollectionsFilters) => {
    setCollectionsLoading(true);
    setCollectionsError(null);
    try {
      const data = await getPaymentFiles(currentFilters);
      setCollectionsData(data);
    } catch (error) {
      setCollectionsError(error instanceof Error ? error.message : 'Failed to load collections');
    } finally {
      setCollectionsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchCollections(filters);
    } else if (!authLoading && !isAuthenticated) {
      setCollectionsError('Please log in to view collections');
      setCollectionsLoading(false);
    }
  }, [authLoading, fetchCollections, filters, isAuthenticated]);

  const handleSortChange = useCallback((sortBy: string, order: 'asc' | 'desc') => {
    setFilters((prev) => ({
      ...prev,
      sortBy,
      sortOrder: order,
    }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setFilters((prev) => ({
      ...prev,
      page,
    }));
  }, []);

  const handlePresetChange = useCallback(
    (presetKey: CollectionPresetKey) => {
      const preset = COLLECTION_PRESETS[presetKey];
      setActivePreset(presetKey);

      const newFilters: CollectionsFilters = {
        ...filters,
        page: 1,
      };

      // Only set status_type_id if preset has status filters
      if (preset.status_type_ids && preset.status_type_ids.length > 0) {
        newFilters.status_type_id = preset.status_type_ids[0];
      } else {
        delete newFilters.status_type_id;
      }

      setFilters(newFilters);
    },
    [filters]
  );

  const handlePaymentFileClick = (paymentFile: PaymentFileItem) => {
    navigate(`/payment-files/${paymentFile.file_id}`, { state: { from: 'escrow' } });
  };

  const getPaymentFileLink = (paymentFile: PaymentFileItem) => ({
    to: `/payment-files/${paymentFile.file_id}`,
    state: { from: 'escrow' },
  });

  const handleDownload = async (file: PaymentFileItem) => {
    try {
      const downloadData = await getDownloadUrl(file.file_id);

      // Open signed URL in new tab
      window.open(downloadData.signed_url, '_blank');

      toast.success(`Downloading ${file.file_name}`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to download file';
      toast.error(errorMessage);
    }
  };

  const handleDisbursementDownload = async (file: DisbursementFile) => {
    try {
      const downloadData = await getDownloadUrl(file.id);

      // Open signed URL in new tab
      window.open(downloadData.signed_url, '_blank');

      const fileName = file.reference || file.id.slice(0, 8);
      toast.success(`Downloading ${fileName}`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to download file';
      toast.error(errorMessage);
    }
  };

  // Determine which preset matches current filter
  const currentPreset = useMemo(() => {
    if (!filters.status_type_id) return 'all';

    for (const key of COLLECTION_PRESET_ORDER) {
      const preset = COLLECTION_PRESETS[key];
      if (preset.status_type_ids?.includes(filters.status_type_id)) {
        return key;
      }
    }
    return activePreset;
  }, [filters.status_type_id, activePreset]);

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
      <div className="px-4 lg:px-6">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold tracking-tight">Escrow</h1>
          <p className="text-sm text-muted-foreground">
            Manage client collections and supplier disbursements
          </p>
        </div>
      </div>

      {/* Collections Section */}
      <section className="mx-4 lg:mx-6 bg-green-50/50 dark:bg-green-950/20 p-6 border border-green-200/50 dark:border-green-800/30">
        <div className="flex items-center gap-2 mb-6">
          <ArrowDownCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
          <h2 className="text-lg font-semibold">Collections</h2>
        </div>

        <div className="space-y-4">
          {/* Preset Filter Buttons */}
          <div className="flex flex-wrap items-center gap-2">
            {COLLECTION_PRESET_ORDER.map((presetKey) => (
              <Button
                key={presetKey}
                type="button"
                size="sm"
                variant={currentPreset === presetKey ? 'default' : 'outline'}
                onClick={() => handlePresetChange(presetKey)}
              >
                {COLLECTION_PRESETS[presetKey].label}
              </Button>
            ))}
          </div>

          <PaymentFilesTable
            caption="Collections"
            data={collectionsData}
            isLoading={collectionsLoading}
            error={collectionsError}
            onFileClick={handlePaymentFileClick}
            getRowLink={getPaymentFileLink}
            onDownload={handleDownload}
            onSortChange={handleSortChange}
            currentSortKey={filters.sortBy ?? 'created_at'}
            currentSortOrder={filters.sortOrder ?? 'desc'}
            onPageChange={handlePageChange}
          />
        </div>
      </section>

      {/* Disbursements Section */}
      <section className="mx-4 lg:mx-6 bg-blue-50/50 dark:bg-blue-950/20 p-6 border border-blue-200/50 dark:border-blue-800/30">
        <div className="flex items-center justify-between gap-2 mb-6">
          <div className="flex items-center gap-2">
            <ArrowUpCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-lg font-semibold">Disbursements</h2>
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="disbursement-status-filter" className="text-sm text-muted-foreground">
              Status
            </label>
            <Select
              value={String(disbursementStatusFilter)}
              onValueChange={(value) => {
                setDisbursementStatusFilter(
                  value === DISBURSEMENT_STATUS_FILTER_ALL
                    ? DISBURSEMENT_STATUS_FILTER_ALL
                    : (Number(value) as FileStatusId)
                );
              }}
            >
              <SelectTrigger
                id="disbursement-status-filter"
                size="sm"
                className="min-w-[180px]"
                aria-label="Filter disbursements by status"
              >
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={DISBURSEMENT_STATUS_FILTER_ALL}>All statuses</SelectItem>
                {DISBURSEMENT_STATUS_FILTER_OPTIONS.map((statusId) => (
                  <SelectItem key={statusId} value={String(statusId)}>
                    {FILE_STATUS_DISPLAY[statusId]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {disbursementFilesLoading ? (
          <div className="text-center py-8 text-muted-foreground">
            Loading disbursement files...
          </div>
        ) : (
          <DisbursementFilesTable
            disbursementFiles={disbursementFiles}
            onRefresh={() => fetchDisbursementFiles(disbursementStatusFilter, true)}
            onDownload={handleDisbursementDownload}
          />
        )}
      </section>
    </div>
  );
};
